import React, { useState, useEffect } from 'react';
import { MessageSquare, User, Calendar, Edit2, Trash2, Send } from 'lucide-react';
import api from '../api/axiosInstance';
import { PermissionManager } from '../utils/PermissionManager';
import './css/error-board-comments.css';

interface Comment {
  id: number;
  boardId: number;
  content: string;
  author: string;
  authorId: number;
  createdAt: string;
  updatedAt?: string;
  isDeleted: string;
}

interface ErrorBoardCommentsProps {
  boardId: number;
  boardAuthorId: number;
  currentUserId: string;
  currentUserName: string;
}

const ErrorBoardComments: React.FC<ErrorBoardCommentsProps> = ({
  boardId,
  boardAuthorId,
  currentUserId,
  currentUserName
}) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editContent, setEditContent] = useState('');
  const [loading, setLoading] = useState(false);

  // 댓글 목록 조회
  const fetchComments = async () => {
    try {
      const response = await api.get(`/api/error-board/${boardId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('댓글 조회 실패:', error);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [boardId]);

  // 댓글 작성
  const handleSubmitComment = async () => {
    if (!newComment.trim()) {
      alert('댓글 내용을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/api/error-board/${boardId}/comments`, {
        content: newComment
      });
      setNewComment('');
      await fetchComments();
    } catch (error) {
      console.error('댓글 작성 실패:', error);
      alert('댓글 작성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 댓글 수정 시작
  const handleStartEdit = (comment: Comment) => {
    setEditingId(comment.id);
    setEditContent(comment.content);
  };

  // 댓글 수정 취소
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditContent('');
  };

  // 댓글 수정 저장
  const handleSaveEdit = async (commentId: number) => {
    if (!editContent.trim()) {
      alert('댓글 내용을 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/api/error-board/comments/${commentId}`, {
        content: editContent
      });
      setEditingId(null);
      setEditContent('');
      await fetchComments();
    } catch (error: any) {
      console.error('댓글 수정 실패:', error);
      if (error.response?.status === 403) {
        alert('댓글을 수정할 권한이 없습니다.');
      } else {
        alert('댓글 수정에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 댓글 삭제
  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm('댓글을 삭제하시겠습니까?')) {
      return;
    }

    setLoading(true);
    try {
      await api.delete(`/api/error-board/comments/${commentId}`);
      await fetchComments();
    } catch (error: any) {
      console.error('댓글 삭제 실패:', error);
      if (error.response?.status === 403) {
        alert('댓글을 삭제할 권한이 없습니다.');
      } else {
        alert('댓글 삭제에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 권한 확인: 작성자 본인 OR 관리자 OR 게시글 작성자
  const canModifyComment = (comment: Comment): boolean => {
    const isAuthor = comment.authorId === Number(currentUserId);
    const isAdmin = PermissionManager.canModifyAllBoardPosts();
    const isBoardAuthor = boardAuthorId === Number(currentUserId);
    return isAuthor || isAdmin || isBoardAuthor;
  };

  // 날짜 포맷팅 - 정확한 시간 표시
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);

    // YYYY.MM.DD HH:mm 형식으로 표시
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${year}.${month}.${day} ${hours}:${minutes}`;
  };

  return (
    <div className="error-board-comments">
      <div className="comments-header">
        <MessageSquare size={20} />
        <h3>댓글 {comments.length}개</h3>
      </div>

      {/* 댓글 목록 */}
      <div className="comments-list">
        {comments.length === 0 ? (
          <div className="comments-empty">
            <MessageSquare size={32} />
            <p>첫 댓글을 남겨보세요</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="comment-item">
              <div className="comment-header">
                <div className="comment-author-info">
                  <User size={16} />
                  <span className="comment-author">{comment.author}</span>
                  <span className="comment-date">
                    <Calendar size={14} />
                    {formatDate(comment.createdAt)}
                  </span>
                  {comment.updatedAt && (
                    <span className="comment-edited">(수정됨)</span>
                  )}
                </div>
                {canModifyComment(comment) && (
                  <div className="comment-actions">
                    {editingId !== comment.id && (
                      <>
                        <button
                          className="comment-action-btn"
                          onClick={() => handleStartEdit(comment)}
                          title="수정"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          className="comment-action-btn delete"
                          onClick={() => handleDeleteComment(comment.id)}
                          title="삭제"
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              {editingId === comment.id ? (
                <div className="comment-edit">
                  <textarea
                    className="comment-edit-textarea"
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    autoFocus
                  />
                  <div className="comment-edit-actions">
                    <button
                      className="comment-edit-cancel"
                      onClick={handleCancelEdit}
                    >
                      취소
                    </button>
                    <button
                      className="comment-edit-save"
                      onClick={() => handleSaveEdit(comment.id)}
                      disabled={loading || !editContent.trim()}
                    >
                      저장
                    </button>
                  </div>
                </div>
              ) : (
                <div className="comment-content">
                  {comment.content.split('\n').map((line, idx) => (
                    <React.Fragment key={idx}>
                      {line}
                      {idx < comment.content.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* 댓글 작성 */}
      <div className="comment-write">
        <div className="comment-write-author">
          <User size={16} />
          <span>{currentUserName}</span>
        </div>
        <textarea
          className="comment-write-textarea"
          placeholder="댓글을 입력하세요"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
        />
        <div className="comment-write-footer">
          <button
            className="comment-submit-btn"
            onClick={handleSubmitComment}
            disabled={loading || !newComment.trim()}
          >
            <Send size={16} />
            <span>등록</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorBoardComments;
